import React, { useMemo, useState } from "react";
import {
  Trophy,
  Target,
  XCircle,
  Shield,
  Undo2,
} from "../../../../components/icons";
import { formatDisplayName } from "../../../../lib/nameFormat";
import {
  describeShootoutRound,
  SHOOTOUT_OUTCOMES,
  type ShootoutOutcome,
  type ShootoutSide,
  type ShootoutState,
} from "../../utils/penaltyShootout";
import type { Match, Player } from "../../types";

/**
 * PenaltyShootoutPanel — logging the shootout, kick by kick.
 *
 * Client ask: "AGregar para lo de los penales, se debe colocar que los
 * jugadores fallan o hacen gol." Until now the cockpit could enter the
 * `PENALTIES` phase and then had nothing to record: the shootout result
 * existed only in the operator's head.
 *
 * Shape of the interaction, in the order the operator needs it under time
 * pressure: the running tally is always visible, the side about to kick is
 * pre-selected by strict alternation (overridable in one tap, because a
 * referee's order is the authority, not ours), the kicker is picked from
 * that team's squad, and the three outcomes are one tap each. Nothing here
 * blocks: a shootout that has mathematically ended still accepts kicks,
 * since the log has to be able to record what actually happened, including
 * mistakes that get corrected in the Review tab afterwards.
 *
 * Anyone in the matchday squad is offerable as a kicker. The laws restrict
 * takers to the players on the pitch when time expired, but the cockpit's
 * on-field set is itself derived from a log that may be mid-correction —
 * refusing to record a real kick because our derived roster disagrees is
 * exactly the trap the substitution editor avoids too. On-field players are
 * listed first so the common case stays a short reach.
 */

interface PenaltyShootoutPanelProps {
  match: Match | null;
  state: ShootoutState;
  /** Ids currently on the pitch — used only to order the kicker list. */
  onFieldIds?: ReadonlySet<string>;
  disabled?: boolean;
  onLogKick: (input: {
    side: ShootoutSide;
    player: Player;
    outcome: ShootoutOutcome;
  }) => void;
  /** Undo the most recent kick; omit to hide the control. */
  onUndoLastKick?: () => void;
  t: any;
}

const OUTCOME_STYLES: Record<ShootoutOutcome, string> = {
  Goal: "bg-emerald-600/25 text-emerald-200 border-emerald-500/50 hover:bg-emerald-600/40",
  Saved: "bg-sky-600/25 text-sky-200 border-sky-500/50 hover:bg-sky-600/40",
  Missed:
    "bg-rose-600/25 text-rose-200 border-rose-500/50 hover:bg-rose-600/40",
};

const OUTCOME_LABELS: Record<ShootoutOutcome, [string, string]> = {
  Goal: ["shootoutOutcomeGoal", "Goal"],
  Saved: ["shootoutOutcomeSaved", "Saved"],
  Missed: ["shootoutOutcomeMissed", "Missed"],
};

const OUTCOME_ICON: Record<ShootoutOutcome, React.ComponentType<any>> = {
  Goal: Target,
  Saved: Shield,
  Missed: XCircle,
};

/** One dot per kick taken: filled for a goal, hollow for anything else. */
function KickDots({
  results,
  side,
}: {
  results: ShootoutOutcome[];
  side: ShootoutSide;
}) {
  return (
    <span
      className="flex flex-wrap items-center gap-1"
      data-testid={`shootout-dots-${side}`}
    >
      {results.map((outcome, i) => (
        <span
          key={i}
          data-testid={`shootout-dot-${side}-${outcome.toLowerCase()}`}
          title={outcome}
          className={`inline-block h-2.5 w-2.5 rounded-full border ${
            outcome === "Goal"
              ? "bg-emerald-400 border-emerald-300"
              : "bg-transparent border-slate-500"
          }`}
        />
      ))}
    </span>
  );
}

export const PenaltyShootoutPanel: React.FC<PenaltyShootoutPanelProps> = ({
  match,
  state,
  onFieldIds,
  disabled = false,
  onLogKick,
  onUndoLastKick,
  t,
}) => {
  // null means "follow the alternation"; a tap pins an explicit side until
  // the next kick is logged.
  const [sideOverride, setSideOverride] = useState<ShootoutSide | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>("");

  const side: ShootoutSide = sideOverride ?? state.nextSide;
  const team = match
    ? side === "home"
      ? match.home_team
      : match.away_team
    : null;

  const kickers: Player[] = useMemo(() => {
    const players = team?.players ?? [];
    const byJersey = (a: Player, b: Player) =>
      (a.jersey_number || 0) - (b.jersey_number || 0);
    if (!onFieldIds) return [...players].sort(byJersey);
    const on = players.filter((p) => onFieldIds.has(p.id)).sort(byJersey);
    const off = players.filter((p) => !onFieldIds.has(p.id)).sort(byJersey);
    return [...on, ...off];
  }, [team, onFieldIds]);

  const selectedPlayer = kickers.find((p) => p.id === selectedPlayerId) ?? null;

  // PMA-COCK-201: derived fresh from `state` on every render — never stored,
  // never used to disable anything. See `describeShootoutRound`'s own doc
  // comment for why `winner` must recompute rather than latch.
  const roundInfo = useMemo(() => describeShootoutRound(state), [state]);

  const chooseSide = (next: ShootoutSide) => {
    setSideOverride(next);
    setSelectedPlayerId("");
  };

  const logKick = (outcome: ShootoutOutcome) => {
    if (disabled || !selectedPlayer) return;
    onLogKick({ side, player: selectedPlayer, outcome });
    setSelectedPlayerId("");
    // Hand the turn back to the alternation for the next kick.
    setSideOverride(null);
  };

  if (!match) return null;

  // PMA-COCK-201: "Ronda N de 5" through the first five rounds, then
  // "Muerte súbita — Ronda N" once both sides have taken five — same wording
  // (and the same i18n keys) the public timeline's shootout header uses, so
  // the two surfaces cannot drift onto different text for the same state.
  const roundLabel =
    roundInfo.phase === "suddenDeath"
      ? t("shootoutSuddenDeathLabel", "Sudden death — Round {{round}}")
          .split("{{round}}")
          .join(String(roundInfo.round))
      : t("shootoutRoundLabel", "Round {{round}} of 5")
          .split("{{round}}")
          .join(String(roundInfo.round));

  // A hint, never a gate (see `resolveShootoutWinner`'s doc comment): every
  // control below stays exactly as enabled/disabled as it already was. Only
  // present while `roundInfo.winner` is non-null, which is recomputed above
  // from `state` on every render — a further kick, or a correction to one
  // already logged, changes `state` and this label follows on the next
  // render without any of this component's own memory of a prior "decided".
  const decidedLabel = roundInfo.winner
    ? t("shootoutDecidedLabel", "{{team}} wins the shootout")
        .split("{{team}}")
        .join(
          roundInfo.winner === "home"
            ? match.home_team.short_name || match.home_team.name
            : match.away_team.short_name || match.away_team.name,
        )
    : null;

  return (
    <section
      className="rounded-xl border border-slate-700 bg-slate-800/80 p-4"
      data-testid="penalty-shootout-panel"
      aria-label={t("shootoutTitle", "Penalty shootout")}
    >
      {/* Header: title + tally */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-200">
          <Trophy size={16} className="text-amber-300" aria-hidden="true" />
          {t("shootoutTitle", "Penalty shootout")}
        </h2>
        <div
          className="flex items-center gap-2 font-mono text-2xl font-bold tabular-nums text-slate-100"
          data-testid="shootout-score"
        >
          <span data-testid="shootout-score-home">{state.home.scored}</span>
          <span className="text-slate-500">-</span>
          <span data-testid="shootout-score-away">{state.away.scored}</span>
        </div>
      </div>

      {/* Decided hint — PMA-COCK-201. Never gates anything below: the side
          picker, kicker select and outcome buttons keep exactly the
          disabled logic they already had. Recomputed every render from
          `state`, so a further kick or a corrected one updates or clears
          this the instant `state` itself changes. */}
      {decidedLabel && (
        <p
          className="mb-3 flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-200"
          data-testid="shootout-decided-label"
          data-winner={roundInfo.winner}
        >
          <Trophy size={12} aria-hidden="true" />
          {decidedLabel}
        </p>
      )}

      {/* Per-team standing + turn selection */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        {(["home", "away"] as ShootoutSide[]).map((teamSide) => {
          const teamState = teamSide === "home" ? state.home : state.away;
          const label =
            teamSide === "home"
              ? match.home_team.short_name || match.home_team.name
              : match.away_team.short_name || match.away_team.name;
          const isActive = side === teamSide;
          return (
            <button
              key={teamSide}
              type="button"
              data-testid={`shootout-side-${teamSide}`}
              aria-pressed={isActive}
              disabled={disabled}
              onClick={() => chooseSide(teamSide)}
              className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-50 ${
                isActive
                  ? "border-teal-500/60 bg-teal-900/20"
                  : "border-slate-700 bg-slate-900/40 hover:border-slate-600"
              }`}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-bold uppercase tracking-wide text-slate-200">
                  {label}
                </span>
                <span className="shrink-0 text-[11px] font-semibold tabular-nums text-slate-400">
                  {teamState.scored}/{teamState.taken}
                </span>
              </span>
              <KickDots results={teamState.results} side={teamSide} />
            </button>
          );
        })}
      </div>

      {/* Turn line */}
      <p
        className="mb-2 text-[11px] uppercase tracking-wider text-slate-400"
        data-testid="shootout-turn"
      >
        {t("shootoutTurn", "Kicking")}:{" "}
        <span className="font-bold text-slate-200">
          {side === "home"
            ? match.home_team.short_name || match.home_team.name
            : match.away_team.short_name || match.away_team.name}
        </span>{" "}
        <span
          className="text-slate-500"
          data-testid="shootout-round-label"
          data-round-phase={roundInfo.phase}
        >
          ({roundLabel})
        </span>
      </p>

      {/* Kicker picker */}
      <div className="mb-3">
        <label
          className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400"
          htmlFor="shootout-kicker"
        >
          {t("shootoutKicker", "Kicker")}
        </label>
        <select
          id="shootout-kicker"
          data-testid="shootout-kicker"
          disabled={disabled}
          value={selectedPlayerId}
          onChange={(e) => setSelectedPlayerId(e.target.value)}
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-50"
        >
          <option value="">{t("selectPlayer", "Select a Player")}</option>
          {kickers.map((p) => (
            <option key={p.id} value={p.id}>
              #{p.jersey_number} {formatDisplayName(p.full_name)}
            </option>
          ))}
        </select>
      </div>

      {/* Outcomes */}
      <div className="grid grid-cols-3 gap-2">
        {SHOOTOUT_OUTCOMES.map((outcome) => {
          const Icon = OUTCOME_ICON[outcome];
          const [key, fallback] = OUTCOME_LABELS[outcome];
          return (
            <button
              key={outcome}
              type="button"
              data-testid={`shootout-outcome-${outcome.toLowerCase()}`}
              disabled={disabled || !selectedPlayer}
              onClick={() => logKick(outcome)}
              className={`flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${OUTCOME_STYLES[outcome]}`}
            >
              <Icon size={13} aria-hidden="true" />
              {t(key, fallback)}
            </button>
          );
        })}
      </div>
      {!selectedPlayer && (
        <p
          className="mt-2 text-[11px] text-slate-400"
          data-testid="shootout-pick-hint"
        >
          {t("shootoutPickKicker", "Pick the kicker to record the outcome.")}
        </p>
      )}

      {/* Kick log */}
      {state.kicks.length > 0 && (
        <div className="mt-3 border-t border-slate-700 pt-3">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span
              className="text-[10px] uppercase tracking-wider text-slate-400"
              data-testid="shootout-kicks"
            >
              {t("shootoutKicks", "Kicks")}
            </span>
            {onUndoLastKick && (
              <button
                type="button"
                data-testid="shootout-undo"
                disabled={disabled}
                onClick={onUndoLastKick}
                className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-[11px] font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                <Undo2 size={11} aria-hidden="true" />
                {t("shootoutUndo", "Undo last")}
              </button>
            )}
          </div>
          <ol className="flex flex-col gap-0.5" data-testid="shootout-kick-log">
            {[...state.kicks].reverse().map((k) => {
              const kickTeam =
                k.side === "home" ? match.home_team : match.away_team;
              const player = kickTeam.players?.find((p) => p.id === k.playerId);
              return (
                <li
                  key={k.key}
                  data-testid="shootout-kick-row"
                  className="flex items-center gap-2 text-xs text-slate-300"
                >
                  <span className="w-5 shrink-0 text-right font-mono text-[10px] text-slate-500">
                    {k.order}
                  </span>
                  <span className="w-10 shrink-0 truncate text-[10px] font-semibold uppercase text-slate-400">
                    {kickTeam.short_name || kickTeam.name}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {player
                      ? `#${player.jersey_number} ${formatDisplayName(player.full_name)}`
                      : (k.playerId ?? "—")}
                  </span>
                  <span
                    className={`shrink-0 text-[11px] font-semibold ${
                      k.outcome === "Goal"
                        ? "text-emerald-300"
                        : "text-rose-300"
                    }`}
                  >
                    {t(
                      OUTCOME_LABELS[k.outcome][0],
                      OUTCOME_LABELS[k.outcome][1],
                    )}
                  </span>
                  {k.pending && (
                    <span className="shrink-0 rounded-full border border-yellow-900/50 bg-yellow-900/30 px-1.5 text-[10px] text-yellow-500">
                      {t("pending", "Pending")}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
};

export default PenaltyShootoutPanel;
